import { createRoute, redirect } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Badge } from '@/client/components/ui/badge';
import { ExternalLink, Info } from 'lucide-react';

const SOURCE_URL = 'https://www.multistate.us/resources/2027-legislative-session-dates';
const LAST_GENERATED = 'October 28, 2026';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'key-dates/legislative-sessions',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: LegislativeSessionsComponent,
});

interface LegislativeSession {
  state: string;
  sessionStart: string | null;
  sessionEnd: string | null;
  introDeadlineHouse: string | null;
  introDeadlineSenate: string | null;
  crossover: string | null;
  carryoverFromPrior: boolean;
  carryoverToNext: boolean;
  notes: string | null;
}

const LEGISLATIVE_SESSIONS_2027: LegislativeSession[] = [
  { state: 'Alaska', sessionStart: '1/19/27', sessionEnd: '5/19/27', introDeadlineHouse: null, introDeadlineSenate: null, crossover: null, carryoverFromPrior: false, carryoverToNext: true, notes: null },
  { state: 'Arizona', sessionStart: '1/11/27', sessionEnd: '5/21/27', introDeadlineHouse: null, introDeadlineSenate: null, crossover: null, carryoverFromPrior: false, carryoverToNext: false, notes: null },
  { state: 'Oregon', sessionStart: '1/11/27', sessionEnd: '6/19/27', introDeadlineHouse: null, introDeadlineSenate: null, crossover: null, carryoverFromPrior: false, carryoverToNext: false, notes: null },
  { state: 'Washington', sessionStart: '1/11/27', sessionEnd: '4/26/27', introDeadlineHouse: null, introDeadlineSenate: null, crossover: null, carryoverFromPrior: false, carryoverToNext: true, notes: null },
  { state: 'Wisconsin', sessionStart: '1/4/27', sessionEnd: '12/31/27', introDeadlineHouse: null, introDeadlineSenate: null, crossover: null, carryoverFromPrior: false, carryoverToNext: true, notes: null },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return dateStr;
}

function LegislativeSessionsComponent() {
  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h3 className="text-2xl font-semibold">2027 Legislative Sessions</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Key dates for state legislative sessions relevant to our clients
            </p>
          </div>
          <a
            href={SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline shrink-0"
          >
            <ExternalLink className="h-4 w-4" />
            View Source (MultiState)
          </a>
        </div>

        <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/50 text-sm">
          <Info className="h-4 w-4 text-muted-foreground shrink-0" />
          <span>
            Last generated on <strong>{LAST_GENERATED}</strong>. Check the source link above for the latest updates.
          </span>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>Session Start</TableHead>
                <TableHead>Session End</TableHead>
                <TableHead>Intro Deadline (House)</TableHead>
                <TableHead>Intro Deadline (Senate)</TableHead>
                <TableHead>Crossover</TableHead>
                <TableHead>Carryover '26 to '27</TableHead>
                <TableHead>Carryover '27 to '28</TableHead>
                <TableHead className="min-w-[200px]">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {LEGISLATIVE_SESSIONS_2027.map((session) => (
                <TableRow key={session.state}>
                  <TableCell className="font-medium">{session.state}</TableCell>
                  <TableCell>{formatDate(session.sessionStart)}</TableCell>
                  <TableCell>{formatDate(session.sessionEnd)}</TableCell>
                  <TableCell>{formatDate(session.introDeadlineHouse)}</TableCell>
                  <TableCell>{formatDate(session.introDeadlineSenate)}</TableCell>
                  <TableCell>{formatDate(session.crossover)}</TableCell>
                  <TableCell>
                    <Badge variant={session.carryoverFromPrior ? 'default' : 'secondary'}>
                      {session.carryoverFromPrior ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={session.carryoverToNext ? 'default' : 'secondary'}>
                      {session.carryoverToNext ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {session.notes || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Carryover:</strong> Indicates if bills can carry over between years (2026 into 2027, and 2027 into 2028)</p>
          <p><strong>Notes:</strong> Special session info, veto recesses, and other important dates</p>
        </div>
      </div>
    </Layout>
  );
}
